import type { FileResponse } from "@/lib/api";

// BRK-S139 — handing a fetched file to the browser to save.
//
// Every file this portal serves is behind a bearer-authenticated, own-scoped
// route, so none of them can be a plain `<a href>`: an anchor's own request
// carries no `Authorization` header and would 401 into an empty tab, with
// nothing on screen saying why. The bytes are fetched (`apiRequestBlob`) and
// handed over here.
//
// THE SERVER NAMES THE FILE. PMS sends `Content-Disposition`, and its name is
// the one that means something — `Program-Indication-SB-01578.pdf` carries the
// order number and is the same string an underwriter sees on the document. A
// name built here from an id would put a uuid in the broker's downloads folder,
// which is why the fallback is a plain readable word and never the id.
//
// THE OBJECT URL IS REVOKED. A blob URL keeps its bytes alive as long as the
// document does, so a broker walking a few accounts would otherwise leave
// several megabytes of PDF pinned in the tab. The revoke is deferred by a tick
// rather than run immediately: revoking in the same task as the synthetic click
// races the browser's own read of the URL in some engines, and a download that
// silently produces nothing is worse than a byte held a moment longer.

/**
 * Save a fetched file, under the server's own name.
 *
 * `fallbackName` is used only when the response carried no
 * `Content-Disposition` — make it a readable word, never an id.
 */
export function saveFile(file: FileResponse, fallbackName: string): void {
  const url = URL.createObjectURL(file.blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = file.filename ?? fallbackName;
  // Appended before clicking: a detached anchor's click is ignored in Firefox.
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
